import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type StatusBadgeTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

const tones: Record<StatusBadgeTone, { box: string; dot: string }> = {
  success: { box: 'bg-success-soft text-success border-success/20', dot: 'bg-success' },
  warning: { box: 'bg-warning-soft text-warning border-warning/25', dot: 'bg-warning' },
  danger: { box: 'bg-danger-soft text-danger border-danger/20', dot: 'bg-danger' },
  neutral: { box: 'bg-surface text-ink-secondary border-line-strong', dot: 'bg-ink-muted' },
  info: { box: 'bg-info-soft text-info border-info/20', dot: 'bg-info' },
};

export interface StatusBadgeProps {
  tone?: StatusBadgeTone;
  label: ReactNode;
  /** Leading status dot. Color is supplementary to the label. */
  dot?: boolean;
  icon?: ReactNode;
  className?: string;
  title?: string;
}

/** Compact status chip with semantic tone. Prefer this over ad-hoc Badge colors for status. */
export function StatusBadge({ tone = 'neutral', label, dot = true, icon, className, title }: StatusBadgeProps) {
  const t = tones[tone];
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium',
        t.box,
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />}
      {icon}
      {label}
    </span>
  );
}
